import React from "react";
import { motion } from "framer-motion";
import {
  Mail,
  MessageSquareWarning,
  FileQuestion,
  HourglassIcon,
  ArrowDown,
  XCircle,
} from "lucide-react";

const PAIN = [
  "Missing specifications",
  "Repeated supplier questions",
  "Incomplete requirements",
  "Wrong quotations",
  "Long email chains",
  "Procurement delays",
];

const FLOW = [
  { icon: Mail, label: "Email", note: "Unstructured request" },
  { icon: MessageSquareWarning, label: "Supplier Questions", note: "5–12 back & forth" },
  { icon: FileQuestion, label: "Missing Information", note: "Gaps reopen the loop" },
  { icon: HourglassIcon, label: "Delayed Quotations", note: "Weeks lost per RFQ" },
];

export default function Problem() {
  return (
    <section
      id="problem"
      data-testid="problem-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow">The Problem</span>
          <h2
            data-testid="problem-headline"
            className="mt-4 font-display font-black tracking-[-0.035em] text-[40px] sm:text-[56px] leading-[0.98] text-[#0A0A0A]"
          >
            Emails don&apos;t create
            <br />
            <span className="text-black/40">professional RFQs.</span>
          </h2>
          <p className="mt-5 text-[15px] sm:text-[17px] leading-relaxed text-black/60 max-w-2xl">
            Procurement teams lose weeks to unstructured email threads.
            Specifications get lost. Suppliers ask the same questions five times.
            Quotations come back wrong. There is a better way.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-stretch">
          {/* Pain list */}
          <div className="rounded-2xl border border-black/[0.06] bg-[#FAFAFB] p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-rose-500" />
              <span className="font-mono text-[10.5px] tracking-[0.2em] text-rose-700">
                WITHOUT A WORKSPACE
              </span>
            </div>
            <ul className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {PAIN.map((p, i) => (
                <motion.li
                  key={p}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.35, delay: i * 0.06 }}
                  className="flex items-start gap-2.5 text-[14.5px] text-black/75"
                >
                  <span className="mt-1 inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
                  {p}
                </motion.li>
              ))}
            </ul>
            <div className="mt-6 rounded-xl bg-white border border-black/[0.06] p-4">
              <div className="font-mono text-[10.5px] tracking-[0.2em] text-black/45">
                AVERAGE LEAD TIME
              </div>
              <div className="mt-1 font-display text-[28px] font-bold tracking-tight">
                3–5 weeks <span className="text-black/40 text-[14px] font-medium ml-1">just to get usable quotes</span>
              </div>
            </div>
          </div>

          {/* Email → delay flow */}
          <div className="relative rounded-2xl border border-black/[0.06] bg-white p-6 sm:p-8 shadow-soft">
            <div className="font-mono text-[10.5px] tracking-[0.2em] text-black/45">
              THE BROKEN FLOW
            </div>
            <ol className="mt-5 space-y-3">
              {FLOW.map(({ icon: Icon, label, note }, i) => (
                <React.Fragment key={label}>
                  <motion.li
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="flex items-center gap-3 rounded-xl border border-black/[0.06] bg-[#FAFAFB] p-3.5"
                  >
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border border-black/[0.06] text-black/70">
                      <Icon className="h-4 w-4" strokeWidth={2} />
                    </span>
                    <div className="flex-1">
                      <div className="font-display text-[15px] font-semibold tracking-tight">
                        {label}
                      </div>
                      <div className="text-[12.5px] text-black/55">{note}</div>
                    </div>
                    <span className="font-mono text-[10px] text-black/35">0{i + 1}</span>
                  </motion.li>
                  {i < FLOW.length - 1 && (
                    <li className="flex justify-center" aria-hidden>
                      <ArrowDown className="h-4 w-4 text-black/25" />
                    </li>
                  )}
                </React.Fragment>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}
